import { useCallback, useEffect, useRef, useState } from "react";

import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";

import {
  breadcrumbFromHierarchy,
  localDateIso,
  recordTeamView,
  selectFrequentTeams,
  type RecordTeamViewInput,
} from "../homeTeamViews";
import { enqueueWorkspaceUserWrite } from "../workspaceUserWriteQueue";
import { useUserPreferences } from "./useUserPreferences";

/**
 * Teams this user opens in this workspace.
 * The visit is applied to homeTeamViews on that scope's snapshot at send time.
 */
export const useHomeTeamViews = () => {
  const {
    loading,
    workspaceId,
    userId,
    mergedPreferences,
    workspaceUserPreferences,
    sendWorkspaceUserPreferences,
    updatePreferencesForWorkspaceUser,
  } = useUserPreferences();

  const preferencesRef = useRef(workspaceUserPreferences);
  const scopeRef = useRef({ workspaceId, userId });
  preferencesRef.current = workspaceUserPreferences;
  scopeRef.current = { workspaceId, userId };

  const recordView = useCallback(
    async (entry: RecordTeamViewInput): Promise<boolean> => {
      try {
        return await enqueueWorkspaceUserWrite({
          scope: scopeRef.current,
          readScope: () => scopeRef.current,
          field: "homeTeamViews",
          readProps: () => preferencesRef.current,
          apply: (latest) => {
            const next = recordTeamView(latest?.homeTeamViews, entry);
            if (!next) {
              return null;
            }
            return {
              ...(latest ?? {}),
              homeTeamViews: next,
            };
          },
          send:
            sendWorkspaceUserPreferences ?? updatePreferencesForWorkspaceUser,
        });
      } catch {
        return false;
      }
    },
    [sendWorkspaceUserPreferences, updatePreferencesForWorkspaceUser]
  );

  return {
    loading,
    teams: selectFrequentTeams(mergedPreferences?.homeTeamViews),
    recordView,
  };
};

export type TeamPageVisit = {
  id?: string | null;
  name?: string | null;
  hierarchy?: string | null;
} | null;

const MAX_RECORD_ATTEMPTS = 3;

/**
 * Records one local day when a team page has loaded. Same-day repeats are ignored.
 * MS-1188: writes `homeTeamViews` only when `enableHomeV2` is true.
 * `TeamViewRecorder` skips this hook entirely while the toggle is off.
 */
export const useRecordTeamView = (team: TeamPageVisit | undefined) => {
  const { enableHomeV2 } = useWorkspaceFeatureFlags();
  const recordVisits = enableHomeV2 === true;
  const { loading, recordView } = useHomeTeamViews();
  const recordedKey = useRef<string | null>(null);
  const attemptKey = useRef<string | null>(null);
  const attempts = useRef(0);
  const [retryTick, setRetryTick] = useState(0);
  const teamId = team?.id || undefined;
  const name = team?.name || undefined;
  const hierarchy = team?.hierarchy ?? undefined;

  useEffect(() => {
    if (!recordVisits || loading || !teamId || !name) {
      return;
    }

    const day = localDateIso(new Date());
    const key = `${teamId}|${day}|${name}|${hierarchy ?? ""}`;
    if (attemptKey.current !== key) {
      attemptKey.current = key;
      attempts.current = 0;
    }
    if (
      recordedKey.current === key ||
      attempts.current >= MAX_RECORD_ATTEMPTS
    ) {
      return;
    }
    recordedKey.current = key;
    attempts.current += 1;

    void recordView({
      teamId,
      name,
      breadcrumb: breadcrumbFromHierarchy(hierarchy, name),
      day,
    }).then((saved) => {
      if (recordedKey.current !== key) {
        return;
      }
      if (saved) {
        attempts.current = 0;
        return;
      }
      recordedKey.current = null;
      setRetryTick((tick) => tick + 1);
    });
  }, [hierarchy, loading, name, recordView, recordVisits, retryTick, teamId]);
};
